import React, { useState, useEffect, useRef } from "react";
import {
  Monitor,
  Cpu,
  Database,
  Server,
  Code2,
  Smartphone,
  Layers,
  RefreshCw,
  Zap,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  RotateCcw,
} from "lucide-react";

/* =========================================================================
   LIVING ARCHITECTURE BLUEPRINT (REAL SVG + HTML + CSS)
   - One-time cinematic sequence:
     Phase 1 (0 to 600ms): Blueprint reveal onto warm off-white #F7F6F2
     Phase 2 (600 to 1,500ms): Applications, Services, Data, Infrastructure assemble
     Phase 3 (1,200 to 2,400ms): Orange SVG paths draw progressively + traveling packets
     Phase 4 (2,200 to 3,000ms): Disciplines activate + Services component highlights
     Phase 5 (3,000ms onward): Stable interactive blueprint state
   - Intersection Observer triggers execution strictly once per page load
   - Zero em/en dashes: only hyphens (-), colons (:), or bullets (•)
   ========================================================================= */

// The 4 Core Architectural Tiers (Central Stack)
// Height = 72px, Gaps = 18.67px. Top = 25px + 8px padding = 33px.
// Exact Y Centers: Applications: 69, Services: 160, Data: 250, Infrastructure: 341.
const ARCHITECTURE_TIERS = [
  {
    id: "applications",
    number: "T-01",
    name: "Applications",
    subtitle: "Client & Presentation Layer",
    tech: "React 19 • Next.js • Flutter • iOS/Android",
    metric: "Sub-Sec TTFB",
    badge: "Presentation",
    icon: Monitor,
    cy: 69,
  },
  {
    id: "services",
    number: "T-02",
    name: "Services & APIs",
    subtitle: "Core Processing & Orchestration",
    tech: "Distributed NestJS • FastAPI • gRPC Contracts",
    metric: "< 12ms P99 Latency",
    badge: "Central Core",
    icon: Cpu,
    cy: 160,
  },
  {
    id: "data",
    number: "T-03",
    name: "Data & Persistence",
    subtitle: "ACID Storage & High-Speed Cache",
    tech: "PostgreSQL Cluster • Redis Cache • Replicas",
    metric: "99.999% ACID",
    badge: "Persistence",
    icon: Database,
    cy: 250,
  },
  {
    id: "infrastructure",
    number: "T-04",
    name: "Infrastructure",
    subtitle: "Cloud Runtime & Edge Orchestration",
    tech: "Docker OCI • Kubernetes K8s • CI/CD Edge",
    metric: "Zero-Downtime",
    badge: "Runtime",
    icon: Server,
    cy: 341,
  },
];

// The 6 Engineering Disciplines
// Left Column: X = 35 to 305 (Width = 270). Right anchor pin at X = 305.
// Right Column: X = 755 to 1025 (Width = 270). Left anchor pin at X = 755.
// Container Height = 360px. 3 cards of Height = 92px. 2 Gaps = 42px.
// Card Centers: Card 0 = 25 + 46 = 71px, Card 1 = 25 + 180 = 205px, Card 2 = 25 + 314 = 339px.
const BLUEPRINT_DISCIPLINES = [
  {
    id: "custom-apps",
    number: "01",
    title: "Custom Web Applications",
    shortTitle: "Web Applications",
    category: "web-mobile",
    side: "left",
    cy: 71,
    connectedTiers: ["applications", "services"],
    summary:
      "Enterprise client applications with sub-second TTFB and strict type-safe UI contracts.",
    metric: "99.98% SLA",
    icon: Code2,
  },
  {
    id: "mobile-engineering",
    number: "02",
    title: "Mobile Applications",
    shortTitle: "Mobile (iOS & Android)",
    category: "web-mobile",
    side: "left",
    cy: 205,
    connectedTiers: ["applications", "services"],
    summary:
      "Native iOS/Android and Flutter apps with background telemetry and offline synchronization.",
    metric: "60 FPS Native",
    icon: Smartphone,
  },
  {
    id: "saas-platforms",
    number: "04",
    title: "SaaS Platform Engineering",
    shortTitle: "SaaS Multi-Tenant",
    category: "cloud-data",
    side: "left",
    cy: 339,
    connectedTiers: ["applications", "services", "data", "infrastructure"],
    summary:
      "Multi-tenant platforms with cryptographically partitioned schemas and automated metering.",
    metric: "100% Isolation",
    icon: Layers,
  },
  {
    id: "distributed-apis",
    number: "03",
    title: "Distributed Backends & APIs",
    shortTitle: "Backends & APIs",
    category: "backends",
    side: "right",
    cy: 71,
    connectedTiers: ["services", "infrastructure"],
    summary:
      "High-throughput microservices with OpenAPI schemas, gRPC, and distributed rate limiting.",
    metric: "< 12ms Latency",
    icon: Server,
  },
  {
    id: "database-pipelines",
    number: "06",
    title: "Database Architecture & Storage",
    shortTitle: "Database Storage",
    category: "cloud-data",
    side: "right",
    cy: 205,
    connectedTiers: ["data", "services"],
    summary:
      "ACID-compliant relational clusters, automated read replication, and sub-5ms Redis cache layers.",
    metric: "< 5ms Read",
    icon: Database,
  },
  {
    id: "legacy-modernization",
    number: "05",
    title: "Legacy Modernization & Refactoring",
    shortTitle: "Legacy Modernization",
    category: "backends",
    side: "right",
    cy: 339,
    connectedTiers: ["services", "data", "infrastructure"],
    summary:
      "Zero-downtime strangler refactoring of legacy monoliths into resilient decoupled services.",
    metric: "0 Downtime",
    icon: RefreshCw,
  },
];

// Pre-calculated vector connection paths between disciplines and central tiers
// Left anchor pins: X = 305. Central Left edge: X = 390.
// Central Right edge: X = 670. Right anchor pins: X = 755.
const BLUEPRINT_PATHS = [
  // 01: Custom Web (Y=71) -> Applications (Y=69) & Services (Y=160)
  {
    disciplineId: "custom-apps",
    tierId: "applications",
    d: "M 305 71 L 390 69",
    length: 90,
  },
  {
    disciplineId: "custom-apps",
    tierId: "services",
    d: "M 305 71 C 347.5 71, 347.5 160, 390 160",
    length: 125,
  },

  // 02: Mobile Apps (Y=205) -> Applications (Y=69) & Services (Y=160)
  {
    disciplineId: "mobile-engineering",
    tierId: "applications",
    d: "M 305 205 C 347.5 205, 347.5 69, 390 69",
    length: 165,
  },
  {
    disciplineId: "mobile-engineering",
    tierId: "services",
    d: "M 305 205 C 347.5 205, 347.5 160, 390 160",
    length: 105,
  },

  // 04: SaaS Platform (Y=339) -> Applications (Y=69), Services (Y=160), Data (Y=250), Infrastructure (Y=341)
  {
    disciplineId: "saas-platforms",
    tierId: "applications",
    d: "M 305 339 C 355 339, 340 69, 390 69",
    length: 290,
  },
  {
    disciplineId: "saas-platforms",
    tierId: "services",
    d: "M 305 339 C 350 339, 345 160, 390 160",
    length: 205,
  },
  {
    disciplineId: "saas-platforms",
    tierId: "data",
    d: "M 305 339 C 347.5 339, 347.5 250, 390 250",
    length: 130,
  },
  {
    disciplineId: "saas-platforms",
    tierId: "infrastructure",
    d: "M 305 339 L 390 341",
    length: 90,
  },

  // 03: Backends & APIs (Y=71) -> Services (Y=160) & Infrastructure (Y=341)
  {
    disciplineId: "distributed-apis",
    tierId: "services",
    d: "M 755 71 C 712.5 71, 712.5 160, 670 160",
    length: 125,
  },
  {
    disciplineId: "distributed-apis",
    tierId: "infrastructure",
    d: "M 755 71 C 712.5 71, 712.5 341, 670 341",
    length: 290,
  },

  // 06: Database Architecture (Y=205) -> Data (Y=250) & Services (Y=160)
  {
    disciplineId: "database-pipelines",
    tierId: "data",
    d: "M 755 205 C 712.5 205, 712.5 250, 670 250",
    length: 105,
  },
  {
    disciplineId: "database-pipelines",
    tierId: "services",
    d: "M 755 205 C 712.5 205, 712.5 160, 670 160",
    length: 105,
  },

  // 05: Legacy Modernization (Y=339) -> Services (Y=160), Data (Y=250), Infrastructure (Y=341)
  {
    disciplineId: "legacy-modernization",
    tierId: "services",
    d: "M 755 339 C 712.5 339, 712.5 160, 670 160",
    length: 205,
  },
  {
    disciplineId: "legacy-modernization",
    tierId: "data",
    d: "M 755 339 C 712.5 339, 712.5 250, 670 250",
    length: 130,
  },
  {
    disciplineId: "legacy-modernization",
    tierId: "infrastructure",
    d: "M 755 339 L 670 341",
    length: 90,
  },
];

export default function LivingBlueprintArchitecture({
  onSelectDiscipline,
  onScopeDiscipline,
  externalActiveDisciplineId = null,
}) {
  const containerRef = useRef(null);

  // Animation Phase State:
  // 0 = Idle (waiting for viewport entrance)
  // 1 = Blueprint reveal (0 to 600 ms)
  // 2 = Architecture assembly (600 to 1,500 ms)
  // 3 = Connection animation (1,200 to 2,400 ms)
  // 4 = Discipline activation (2,200 to 3,000 ms)
  // 5 = Interactive state (3,000 ms onward)
  const [animationPhase, setAnimationPhase] = useState(0);
  const [isServicesHighlighted, setIsServicesHighlighted] = useState(false);
  const [hoveredDisciplineId, setHoveredDisciplineId] = useState(null);
  const [pinnedDisciplineId, setPinnedDisciplineId] = useState(null);

  // Strict one-time trigger ref
  const hasTriggeredRef = useRef(false);
  const timersRef = useRef([]);

  // Clear all pending timeouts on unmount
  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Check prefers-reduced-motion
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          observer.disconnect(); // Strict single execution rule!

          if (prefersReducedMotion) {
            // Instantly settle without motion if reduced-motion preferred
            setAnimationPhase(5);
            return;
          }

          // Phase 1: Blueprint reveal, 0 to 600 ms
          setAnimationPhase(1);

          // Phase 2: Architecture assembly, 600 to 1,500 ms
          timersRef.current.push(
            setTimeout(() => {
              setAnimationPhase(2);
            }, 600)
          );

          // Phase 3: Connection animation, 1,200 to 2,400 ms
          timersRef.current.push(
            setTimeout(() => {
              setAnimationPhase(3);
            }, 1200)
          );

          // Phase 4: Discipline activation, 2,200 to 3,000 ms
          timersRef.current.push(
            setTimeout(() => {
              setAnimationPhase(4);
              setIsServicesHighlighted(true);
            }, 2200)
          );

          // Central Services pulse settles at 2800 ms
          timersRef.current.push(
            setTimeout(() => {
              setIsServicesHighlighted(false);
            }, 2800)
          );

          // Phase 5: Interactive state, approximately 3 seconds onward
          timersRef.current.push(
            setTimeout(() => {
              setAnimationPhase(5);
            }, 3000)
          );
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px 50px 0px",
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      clearTimers();
    };
  }, []);

  // Active discipline logic (pinned click state overrides external/hover)
  const activeDisciplineId =
    pinnedDisciplineId || externalActiveDisciplineId || hoveredDisciplineId;
  const activeDiscipline = BLUEPRINT_DISCIPLINES.find(
    (d) => d.id === activeDisciplineId
  );

  const isTierActive = (tierId) => {
    if (!activeDiscipline) return false;
    return activeDiscipline.connectedTiers.includes(tierId);
  };

  const isPathActive = (path) => {
    if (!activeDisciplineId) return false;
    return path.disciplineId === activeDisciplineId;
  };

  const isInteractive = animationPhase >= 5;

  const handleDisciplineClick = (discipline) => {
    if (!isInteractive) return;
    if (pinnedDisciplineId === discipline.id) {
      setPinnedDisciplineId(null);
    } else {
      setPinnedDisciplineId(discipline.id);
      if (onSelectDiscipline) {
        onSelectDiscipline(discipline.id);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full relative rounded-3xl bg-[#FAF9F5] border border-neutral-300/80 shadow-[0_12px_40px_rgba(0,0,0,0.04)] overflow-hidden p-4 sm:p-7 select-none transition-all duration-300"
    >
      {/* Blueprint Technical Ambient Millimeter Grid */}
      <div
        className="absolute inset-0 opacity-[0.045] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #000000 1px, transparent 1px), linear-gradient(to bottom, #000000 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Top Architectural Header / Status Telemetry Readout */}
      <div className="relative z-20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 mb-5 border-b border-neutral-200/80">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-neutral-200/90 shadow-2xs">
            <span
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                animationPhase === 0
                  ? "bg-neutral-300"
                  : isInteractive
                  ? "bg-emerald-500 animate-pulse"
                  : "bg-[#FF5A1F] animate-ping"
              }`}
            />
            <span className="text-[11px] font-mono font-bold tracking-wider uppercase text-neutral-800">
              {animationPhase === 0
                ? "BLUEPRINT INITIALIZING"
                : animationPhase < 5
                ? `ASSEMBLING ARCHITECTURE (PHASE ${animationPhase}/4)`
                : activeDiscipline
                ? `INSPECTING // ${activeDiscipline.number} ${activeDiscipline.shortTitle.toUpperCase()}`
                : "LIVING BLUEPRINT // ALL PATHWAYS STABLE"}
            </span>
          </div>

          <span className="hidden md:inline-block text-[11px] font-mono text-neutral-400">
            REV 2.4 • ENTERPRISE ARCHITECTURE
          </span>
        </div>

        {/* Dynamic Context Readout */}
        <div className="flex items-center gap-2">
          {activeDiscipline ? (
            <div className="flex items-center gap-2 text-xs font-sans text-neutral-700 bg-white/95 border border-[#FF5A1F]/30 px-3.5 py-1.5 rounded-full shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
              <span className="font-bold text-[#0B0B0B]">
                {activeDiscipline.metric}
              </span>
              <span className="text-neutral-400">|</span>
              <span className="text-neutral-700 font-medium line-clamp-1 max-w-[280px]">
                {activeDiscipline.summary}
              </span>
              {pinnedDisciplineId && (
                <button
                  onClick={() => setPinnedDisciplineId(null)}
                  className="ml-1 text-[10px] font-mono text-neutral-400 hover:text-[#0B0B0B] underline cursor-pointer"
                  title="Unlock overview"
                >
                  Reset
                </button>
              )}
            </div>
          ) : (
            <div className="text-[11px] font-mono text-neutral-500 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5A1F]" />
              <span>
                {isInteractive
                  ? "Hover or tap any discipline to illuminate connection pathways"
                  : "Initializing vector schematics..."}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Vector Architecture Canvas (Desktop & Tablet Schematic) */}
      <div className="relative w-full max-w-[1060px] mx-auto overflow-hidden">
        {/* SVG Vector Layer for Connection Conduits and Packets */}
        <svg
          viewBox="0 0 1060 410"
          className="w-full h-auto block relative z-10 pointer-events-none"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {/* Orange Glow Filter for Active Conduit Lines */}
            <filter
              id="blueprintOrangeGlow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="0"
                stdDeviation="3.5"
                floodColor="#FF5A1F"
                floodOpacity="0.75"
              />
            </filter>

            {/* Subtle Grid Markings */}
            <pattern
              id="schematicFineDots"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="2" cy="2" r="0.75" fill="#C8C6BC" opacity="0.4" />
            </pattern>
          </defs>

          {/* Schematic Fine Dot Canvas Background */}
          <rect
            x="305"
            y="20"
            width="450"
            height="370"
            fill="url(#schematicFineDots)"
            className={`transition-opacity duration-700 ${
              animationPhase >= 1 ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Central Architecture Outer Conduit Bounding Box (Phase 1) */}
          <rect
            x="390"
            y="25"
            width="280"
            height="360"
            rx="16"
            fill="none"
            stroke="#D8D6CC"
            strokeWidth="1"
            strokeDasharray="4 4"
            className={`transition-opacity duration-700 ${
              animationPhase >= 1 ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Fine Technical Corner Crosshairs (Phase 1) */}
          <g
            className={`transition-opacity duration-500 ${
              animationPhase >= 1 ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Top-Left */}
            <path d="M 382 35 L 398 35 M 390 27 L 390 43" stroke="#A8A69C" strokeWidth="1" />
            {/* Top-Right */}
            <path d="M 662 35 L 678 35 M 670 27 L 670 43" stroke="#A8A69C" strokeWidth="1" />
            {/* Bottom-Left */}
            <path d="M 382 377 L 398 377 M 390 369 L 390 385" stroke="#A8A69C" strokeWidth="1" />
            {/* Bottom-Right */}
            <path d="M 662 377 L 678 377 M 670 369 L 670 385" stroke="#A8A69C" strokeWidth="1" />
          </g>

          {/* ===============================================================
              PHASE 3 & INTERACTIVE: CONNECTION PATHS
              Progressive stroke drawing & selective illumination
              =============================================================== */}
          <g id="conduit-paths">
            {BLUEPRINT_PATHS.map((path, idx) => {
              const active = isPathActive(path);
              const pathId = `blueprint-path-${idx}`;

              // Progressive draw during phase 3 or settled in phase 4/5
              const isDrawn = animationPhase >= 3;
              const isEntering = animationPhase === 3;

              let strokeColor = "#D4D2C8";
              let strokeWidth = 1.25;
              let filter = "none";
              let opacity = 0.8;

              if (isEntering) {
                strokeColor = "#FF5A1F";
                strokeWidth = 2;
                filter = "url(#blueprintOrangeGlow)";
                opacity = 1;
              } else if (isInteractive) {
                if (activeDisciplineId) {
                  if (active) {
                    strokeColor = "#FF5A1F";
                    strokeWidth = 2.5;
                    filter = "url(#blueprintOrangeGlow)";
                    opacity = 1;
                  } else {
                    opacity = 0.15;
                    strokeColor = "#E2E0D8";
                  }
                } else {
                  strokeColor = "#D4D2C8";
                  opacity = 0.75;
                }
              }

              return (
                <g key={idx}>
                  {/* Base Vector Path */}
                  <path
                    id={pathId}
                    d={path.d}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    filter={filter}
                    opacity={opacity}
                    style={{
                      strokeDasharray: path.length,
                      strokeDashoffset: isDrawn ? 0 : path.length,
                      transition:
                        animationPhase <= 3
                          ? "stroke-dashoffset 1100ms cubic-bezier(0.25, 1, 0.5, 1), stroke 400ms, opacity 400ms"
                          : "stroke 250ms, stroke-width 250ms, opacity 250ms",
                    }}
                  />

                  {/* Travelling Orange Data Packets */}
                  {/* Shown during Phase 3 entrance OR when path is active in interactive mode */}
                  {(isEntering || (isInteractive && active)) && (
                    <circle r="3.5" fill="#FF5A1F" filter="url(#blueprintOrangeGlow)">
                      <animateMotion
                        dur={active ? "1.4s" : "2.0s"}
                        repeatCount="indefinite"
                        path={path.d}
                      />
                    </circle>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* HTML / React Interactive Nodes Layer (Positioned seamlessly on top of SVG) */}
        <div className="absolute inset-0 pointer-events-auto">
          {/* ===============================================================
              CENTRAL ARCHITECTURE CORE: 4 TIERS
              Revealed in Phase 2 in sequence (Applications, Services, Data, Infrastructure)
              =============================================================== */}
          <div className="absolute left-[390px] w-[280px] top-[25px] h-[360px] flex flex-col justify-between py-2 z-20">
            {ARCHITECTURE_TIERS.map((tier, idx) => {
              const active = isTierActive(tier.id);
              const isCentralService = tier.id === "services";
              const isRevealed = animationPhase >= 2;
              const highlightServices = isCentralService && isServicesHighlighted;

              return (
                <div
                  key={tier.id}
                  className={`relative p-2.5 sm:p-3 rounded-xl border transition-all duration-500 ${
                    !isRevealed
                      ? "opacity-0 translate-y-3 pointer-events-none"
                      : "opacity-100 translate-y-0"
                  } ${
                    highlightServices
                      ? "bg-white border-[#FF5A1F] shadow-[0_0_28px_rgba(255,90,31,0.4)] ring-2 ring-[#FF5A1F] scale-[1.03]"
                      : active
                      ? "bg-white border-[#FF5A1F] shadow-[0_4px_18px_rgba(255,90,31,0.22)] ring-2 ring-[#FF5A1F]/35"
                      : activeDisciplineId
                      ? "bg-white/60 border-neutral-200/60 opacity-30"
                      : "bg-white border-neutral-200/90 shadow-2xs hover:border-neutral-300"
                  }`}
                  style={{
                    height: "72px",
                    transitionDelay: isRevealed ? `${idx * 160 + 50}ms` : "0ms",
                  }}
                >
                  {/* Left Connection Anchor Dot */}
                  <div
                    className={`absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white transition-colors duration-200 ${
                      highlightServices || active
                        ? "bg-[#FF5A1F] shadow-[0_0_8px_rgba(255,90,31,0.6)]"
                        : "bg-neutral-300"
                    }`}
                  />

                  {/* Right Connection Anchor Dot */}
                  <div
                    className={`absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white transition-colors duration-200 ${
                      highlightServices || active
                        ? "bg-[#FF5A1F] shadow-[0_0_8px_rgba(255,90,31,0.6)]"
                        : "bg-neutral-300"
                    }`}
                  />

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-6 h-6 rounded-lg grid place-items-center transition-colors ${
                          highlightServices || active
                            ? "bg-[#FF5A1F] text-white"
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        <tier.icon className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-[#0B0B0B] leading-none">
                            {tier.name}
                          </h4>
                          <span className="text-[9.5px] font-mono px-1.5 py-0.2 rounded bg-neutral-100 text-neutral-500 font-medium">
                            {tier.number}
                          </span>
                        </div>
                        <p className="text-[10px] text-neutral-500 font-sans mt-0.5 leading-none">
                          {tier.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <span
                        className={`text-[10px] font-mono font-bold block ${
                          highlightServices || active
                            ? "text-[#FF5A1F]"
                            : "text-[#0B0B0B]"
                        }`}
                      >
                        {tier.metric}
                      </span>
                      <span className="text-[8.5px] font-mono text-neutral-400">
                        {tier.badge}
                      </span>
                    </div>
                  </div>

                  {/* Micro Tech Tags */}
                  <div className="mt-1.5 pt-1 border-t border-neutral-100 flex items-center justify-between text-[9px] font-mono text-neutral-400">
                    <span className="truncate max-w-[190px]">{tier.tech}</span>
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        highlightServices || active
                          ? "bg-[#FF5A1F] animate-pulse"
                          : "bg-emerald-400"
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===============================================================
              LEFT WING DISCIPLINE NODES (01 Web, 02 Mobile, 04 SaaS)
              Revealed in Phase 4 with leader lines
              =============================================================== */}
          <div className="absolute left-[35px] w-[270px] top-[25px] h-[360px] flex flex-col justify-between z-20">
            {BLUEPRINT_DISCIPLINES.filter((d) => d.side === "left").map(
              (discipline, idx) => {
                const isActive = activeDisciplineId === discipline.id;
                const isRevealed = animationPhase >= 4;

                return (
                  <div
                    key={discipline.id}
                    onClick={() => handleDisciplineClick(discipline)}
                    onMouseEnter={() =>
                      isInteractive && setHoveredDisciplineId(discipline.id)
                    }
                    onMouseLeave={() =>
                      isInteractive && setHoveredDisciplineId(null)
                    }
                    onFocus={() =>
                      isInteractive && setHoveredDisciplineId(discipline.id)
                    }
                    onBlur={() =>
                      isInteractive && setHoveredDisciplineId(null)
                    }
                    tabIndex={isInteractive ? 0 : -1}
                    role="button"
                    aria-label={`${discipline.number}: ${discipline.title}`}
                    className={`relative p-3 rounded-2xl border transition-all duration-300 text-left outline-none ${
                      !isRevealed
                        ? "opacity-0 -translate-x-4 pointer-events-none"
                        : "opacity-100 translate-x-0"
                    } ${
                      isInteractive ? "cursor-pointer" : "cursor-default"
                    } ${
                      isActive
                        ? "bg-white border-[#FF5A1F] shadow-[0_8px_24px_rgba(255,90,31,0.24)] ring-2 ring-[#FF5A1F]/35 scale-[1.02]"
                        : activeDisciplineId
                        ? "bg-white/60 border-neutral-200/70 opacity-30"
                        : "bg-white border-neutral-200/90 shadow-2xs hover:border-[#FF5A1F]/60 hover:shadow-xs"
                    }`}
                    style={{ height: "92px" }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded-lg grid place-items-center transition-colors ${
                            isActive
                              ? "bg-[#FF5A1F] text-white"
                              : "bg-neutral-100 text-neutral-600 group-hover:text-[#FF5A1F]"
                          }`}
                        >
                          <discipline.icon className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <span className="text-[10px] font-mono text-neutral-400 font-semibold block leading-none">
                            DOMAIN // {discipline.number}
                          </span>
                          <h4 className="text-xs font-bold text-[#0B0B0B] mt-0.5 leading-snug">
                            {discipline.shortTitle}
                          </h4>
                        </div>
                      </div>

                      <span
                        className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                          isActive
                            ? "bg-[#FF5A1F]/10 text-[#FF5A1F]"
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        {discipline.metric}
                      </span>
                    </div>

                    <p className="text-[10px] text-neutral-500 font-sans line-clamp-2 leading-relaxed">
                      {discipline.summary}
                    </p>

                    {/* Right Anchor Indicator Pin */}
                    <div
                      className={`absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white transition-colors duration-200 ${
                        isActive
                          ? "bg-[#FF5A1F] shadow-[0_0_8px_rgba(255,90,31,0.6)]"
                          : "bg-neutral-300"
                      }`}
                    />
                  </div>
                );
              }
            )}
          </div>

          {/* ===============================================================
              RIGHT WING DISCIPLINE NODES (03 Backends, 06 Database, 05 Legacy)
              Revealed in Phase 4 with leader lines
              =============================================================== */}
          <div className="absolute left-[755px] w-[270px] top-[25px] h-[360px] flex flex-col justify-between z-20">
            {BLUEPRINT_DISCIPLINES.filter((d) => d.side === "right").map(
              (discipline, idx) => {
                const isActive = activeDisciplineId === discipline.id;
                const isRevealed = animationPhase >= 4;

                return (
                  <div
                    key={discipline.id}
                    onClick={() => handleDisciplineClick(discipline)}
                    onMouseEnter={() =>
                      isInteractive && setHoveredDisciplineId(discipline.id)
                    }
                    onMouseLeave={() =>
                      isInteractive && setHoveredDisciplineId(null)
                    }
                    onFocus={() =>
                      isInteractive && setHoveredDisciplineId(discipline.id)
                    }
                    onBlur={() =>
                      isInteractive && setHoveredDisciplineId(null)
                    }
                    tabIndex={isInteractive ? 0 : -1}
                    role="button"
                    aria-label={`${discipline.number}: ${discipline.title}`}
                    className={`relative p-3 rounded-2xl border transition-all duration-300 text-left outline-none ${
                      !isRevealed
                        ? "opacity-0 translate-x-4 pointer-events-none"
                        : "opacity-100 translate-x-0"
                    } ${
                      isInteractive ? "cursor-pointer" : "cursor-default"
                    } ${
                      isActive
                        ? "bg-white border-[#FF5A1F] shadow-[0_8px_24px_rgba(255,90,31,0.24)] ring-2 ring-[#FF5A1F]/35 scale-[1.02]"
                        : activeDisciplineId
                        ? "bg-white/60 border-neutral-200/70 opacity-30"
                        : "bg-white border-neutral-200/90 shadow-2xs hover:border-[#FF5A1F]/60 hover:shadow-xs"
                    }`}
                    style={{ height: "92px" }}
                  >
                    {/* Left Anchor Indicator Pin */}
                    <div
                      className={`absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white transition-colors duration-200 ${
                        isActive
                          ? "bg-[#FF5A1F] shadow-[0_0_8px_rgba(255,90,31,0.6)]"
                          : "bg-neutral-300"
                      }`}
                    />

                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded-lg grid place-items-center transition-colors ${
                            isActive
                              ? "bg-[#FF5A1F] text-white"
                              : "bg-neutral-100 text-neutral-600"
                          }`}
                        >
                          <discipline.icon className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <span className="text-[10px] font-mono text-neutral-400 font-semibold block leading-none">
                            DOMAIN // {discipline.number}
                          </span>
                          <h4 className="text-xs font-bold text-[#0B0B0B] mt-0.5 leading-snug">
                            {discipline.shortTitle}
                          </h4>
                        </div>
                      </div>

                      <span
                        className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                          isActive
                            ? "bg-[#FF5A1F]/10 text-[#FF5A1F]"
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        {discipline.metric}
                      </span>
                    </div>

                    <p className="text-[10px] text-neutral-500 font-sans line-clamp-2 leading-relaxed">
                      {discipline.summary}
                    </p>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Quick Select Strip (< 1024px) */}
      <div className="mt-5 pt-4 border-t border-neutral-200/80 block lg:hidden">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs font-mono font-bold text-neutral-600">
            TAP TO TRACE DISCIPLINE:
          </span>
          {pinnedDisciplineId && (
            <button
              onClick={() => setPinnedDisciplineId(null)}
              className="text-xs font-mono text-[#FF5A1F] hover:underline cursor-pointer"
            >
              Reset Overview
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          {BLUEPRINT_DISCIPLINES.map((d) => {
            const isSel = activeDisciplineId === d.id;
            return (
              <button
                key={d.id}
                onClick={() => handleDisciplineClick(d)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                  isSel
                    ? "bg-[#FF5A1F] text-white border-[#FF5A1F] shadow-xs"
                    : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <span className="font-mono opacity-70 mr-1.5">{d.number}</span>
                <span>{d.shortTitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Technical SLA Guarantee Banner */}
      <div className="mt-5 pt-3.5 border-t border-neutral-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-600">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span className="font-mono font-medium">99.99% Architecture SLA</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-[#FF5A1F]" />
            <span className="font-mono font-medium">Zero-Downtime Deployment</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-neutral-700" />
            <span className="font-mono font-medium">Cryptographic Tenant Isolation</span>
          </div>
        </div>

        {activeDiscipline && onScopeDiscipline && (
          <button
            onClick={() => onScopeDiscipline(activeDiscipline)}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5A1F] hover:text-[#e04e18] transition-colors cursor-pointer ml-auto"
          >
            <span>Scope {activeDiscipline.shortTitle}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
